import axios from 'axios';

const BASE = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;

export const API = axios.create({ baseURL: `${BASE}/api` });

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('nslive_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('nslive_token');
      localStorage.removeItem('nslive_user');
      window.location.href = '/';
    }
    return Promise.reject(err);
  }
);

export interface AuthUser {
  sub: string;
  role: 'admin' | 'streamer';
  user_id: number;
  streamer_id: number | null;
  permission: 'read' | 'write' | null;
}

export interface Streamer {
  id: number;
  username: string;
  display_name: string;
  enabled: number;
  pc_connected: boolean;
  overlay_file: string | null;
  active_theme_id: number | null;
  active_game_id: number | null;
  avatar_url?: string | null;
  live_running?: boolean;
  live_status?: 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected';
  tiktok_live?: boolean;
  period?: string | null;
  diamonds?: number | null;
  live_duration_minutes?: number | null;
  live_days?: number | null;
  live_count?: number | null;
  statut_echelon?: string | null;
  statut_evolution?: string | null;
  remu_niveau?: number | null;
  remu_activite?: number | null;
  remu_progressif?: number | null;
  remu_total?: number | null;
  new_followers?: number | null;
  streamer_groupe?: string | null;
  leader_role?: string | null;
  leader_groupe?: string | null;
}

export interface LiveStreamer {
  id: number;
  username: string;
  display_name: string;
  avatar_url?: string | null;
  tiktok_live: boolean;
  viewers?: number;
}

export interface FollowSuggestion {
  id: number;
  username: string;
  display_name: string;
  avatar_url?: string | null;
  is_live: boolean;
  is_followed: boolean;
  viewers?: number;
}

export interface LiveSession {
  id: number;
  streamer_id: number;
  started_at: string;
  ended_at: string | null;
  max_viewers: number;
  new_followers: number;
  total_coins: number;
  source?: string | null;
}

export interface LiveDonor {
  donor_username: string;
  donor_nickname: string;
  donor_avatar_url?: string | null;
  coins: number;
  gifts_count: number;
  last_gift_at: string;
  streamer_id?: number | null;
  is_live?: boolean;
  viewers?: number;
  profile_url?: string | null;
}

export interface TopLiveDonor extends LiveDonor {
}

export interface LiveFollower {
  follower_username: string;
  follower_nickname?: string | null;
  follower_avatar_url?: string | null;
  username: string;
  nickname?: string | null;
  avatar_url?: string | null;
  followed_at?: string | null;
  profile_url?: string | null;
}

export interface Rule {
  id: number;
  streamer_id: number;
  gift_name: string;
  animation_file: string;
  enabled: number;
  pos_x: number;
  pos_y: number;
  pos_w: number;
  pos_h: number;
  game_action_id: number | null;
  tiktok_gift_id: number | null;
  gift_image_url: string | null;
}

export interface AppUser {
  id: number;
  username: string;
  role: string;
  streamer_id: number | null;
  permission: string;
}

export interface Theme {
  id: number;
  name: string;
  slug: string;
  image_url: string | null;
  overlay_file: string | null;
  animation_count?: number;
}

export interface ThemeAnimation {
  id: number;
  theme_id: number;
  name: string;
  animation_file: string | null;
  animation_type: 'video' | 'audio' | 'text' | 'text_dynamic' | 'gift_grid' | 'queue' | 'raffle' | 'mobile_comments' | 'banner';
  trigger_type?: 'auto' | 'manual';
  text_content?: string | null;
  text_color?: string;
  text_size?: number;
  text_effect?: string;
  text_duration?: number;
  text_font?: string;
  text_loops?: number;
  text_bg_color?: string;
  text_bg_opacity?: number;
  text_bg_anim?: string;
  show_gift_image?: number;
  gift_img_x?: number;
  gift_img_y?: number;
  gift_img_w?: number;
  gift_img_h?: number;
  gift_label_pos?: string;
  gift_label_color?: string;
  gift_label_size?: number;
  gift_cols?: number;
  queue_max_visible?: number;
  queue_show_avatar?: number | boolean;
  queue_show_counter?: number | boolean;
  queue_appear_anim?: string;
  queue_disappear_anim?: string;
  queue_auto_hide?: number;
  queue_particle?: string;
  queue_sound?: string | null;
  queue_min_coins?: number;
  queue_bg_image?: string | null;
  queue_q_bg_image?: string | null;
  queue_q_bg_color?: string | null;
  queue_q_pos_x?: number;
  queue_q_pos_y?: number;
  queue_q_pos_w?: number;
  queue_q_pos_h?: number;
  queue_ctrl_pos_x?: number;
  queue_ctrl_pos_y?: number;
  queue_ctrl_pos_w?: number;
  queue_ctrl_pos_h?: number;
  queue_show_gift?: number | boolean;
  queue_gift_pos_x?: number;
  queue_gift_pos_y?: number;
  queue_gift_pos_w?: number;
  queue_gift_pos_h?: number;
  queue_gift_label?: string | null;
  queue_gift_label_pos?: string;
  queue_gift_label_color?: string;
  queue_gift_label_size?: number;
  queue_show_pgift?: number | boolean;
  queue_pgift_pos_x?: number;
  queue_pgift_pos_y?: number;
  queue_pgift_pos_w?: number;
  queue_pgift_pos_h?: number;
  queue_pgift_label?: string | null;
  queue_pgift_label_pos?: string;
  queue_pgift_label_color?: string;
  queue_pgift_label_size?: number;
  queue_tts_enabled?: number;
  queue_tts_voice_id?: string | null;
  mobile_comment_voice_id?: string | null;
  mobile_comment_voice_name?: string | null;
  pos_x: number;
  pos_y: number;
  pos_w: number;
  pos_h: number;
  sort_order: number;
}

export interface StaticElement {
  id: number;
  theme_id: number;
  name: string;
  image_file: string;
  pos_x: number;
  pos_y: number;
  pos_w: number;
  pos_h: number;
  layer: 'above' | 'below';
  sort_order: number;
}

export interface ManualAnimation {
  id: number;
  name: string;
  animation_file: string;
  animation_type: 'video' | 'audio' | 'text' | 'text_dynamic';
  trigger_type: 'manual';
  pos_x: number; pos_y: number; pos_w: number; pos_h: number;
  text_content?: string;
  text_color?: string;
  text_size?: number;
  text_effect?: string;
  text_duration?: number;
  text_font?: string;
  text_loops?: number;
  text_bg_color?: string;
  text_bg_opacity?: number;
  text_bg_anim?: string;
}

export interface StreamerTheme extends Theme {
  is_active: number;
  animation_count: number;
}

export interface AnimationAssignment {
  theme_animation_id: number;
  theme_id: number;
  name: string;
  animation_file: string;
  volume?: number | null;
  animation_type: 'video' | 'audio' | 'text' | 'text_dynamic' | 'queue' | 'raffle' | 'mobile_comments' | 'banner';
  trigger_type: 'auto' | 'manual';
  text_content?: string | null;
  text_color?: string | null;
  text_size?: number | null;
  text_effect?: string | null;
  text_duration?: number | null;
  text_font?: string | null;
  text_loops?: number | null;
  pos_x: number;
  pos_y: number;
  pos_w: number;
  pos_h: number;
  sort_order: number;
  assignment_id: number | null;
  gift_name: string | null;
  tiktok_gift_id: number | null;
  gift_image_url: string | null;
  priority_gift_name?: string | null;
  priority_tiktok_gift_id?: number | null;
  priority_gift_image_url?: string | null;
  banner_enabled?: number | boolean | null;
  banner_config?: string | null;
  show_gift_image?: number;
  gift_img_x?: number | null;
  gift_img_y?: number | null;
  gift_img_w?: number | null;
  gift_img_h?: number | null;
  queue_max_visible?: number | null;
  queue_bg_image?: string | null;
  queue_q_bg_image?: string | null;
  queue_q_bg_color?: string | null;
  queue_q_pos_x?: number | null;
  queue_q_pos_y?: number | null;
  queue_q_pos_w?: number | null;
  queue_q_pos_h?: number | null;
  queue_show_gift?: number | boolean | null;
  queue_gift_pos_x?: number | null;
  queue_gift_pos_y?: number | null;
  queue_gift_pos_w?: number | null;
  queue_gift_pos_h?: number | null;
  queue_gift_label?: string | null;
  queue_gift_label_pos?: string | null;
  queue_gift_label_color?: string | null;
  queue_gift_label_size?: number | null;
}

export interface Game {
  id: number;
  name: string;
  slug: string;
  image_url: string | null;
  action_count?: number;
  platforms?: string;   // ex: "pc,ps,xbox"
}

export interface GameAction {
  id: number;
  game_id: number;
  name: string;
  description: string;
  action_type: string;
  action_value: string;
  duration_ms: number;
  sort_order: number;
  streamer_id?: number | null;
  source_action_id?: number | null;
  is_custom?: number;
  gamepad_value?: string | null;   // bouton manette (Remote Play)
}

export interface Gift {
  id: number;
  name: string;
  name_fr: string | null;
  image_url: string | null;
  coin_value: number;
  tiktok_gift_id: number | null;
}

export interface GiftGameRule {
  id: number;
  streamer_id: number;
  game_id: number;
  gift_name: string;
  game_action_id: number;
  action_name: string;
  action_type: string;
  action_value: string;
  duration_ms: number;
  tiktok_gift_id: number | null;
  gift_image_url: string | null;
}

export interface ThemeStreamer {
  id: number;
  username: string;
  display_name: string;
  avatar_url?: string | null;
  has_theme: number;
}

export interface UserProfile {
  id: number;
  username: string;
  role: string;
  permission?: string | null;
  tiktok_username: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  streamer_id?: number | null;
  can_manage_matchs?: number;
  can_manage_events?: number;
  can_manage_lives?: number;
  can_manage_themes?: number;
}

export interface AccessInfo {
  is_admin: boolean;
  is_responsable: boolean;
  groupes: string[];
  can_manage_matchs: boolean;
  can_manage_events: boolean;
  can_manage_lives: boolean;
  can_manage_themes: boolean;
}

// Auth
export const login = (username: string, password: string) =>
  API.post<{ access_token: string; role: string; streamer_id: number | null; permission: string; username: string }>(
    '/auth/login', { username, password }
  );
export const getMe = () => API.get<AuthUser>('/auth/me');
export const changePassword = (current_password: string, new_password: string) =>
  API.post('/auth/change-password', { current_password, new_password });
export const getProfile = () => API.get<UserProfile>('/auth/profile');
export const updateProfile = (data: Partial<Pick<UserProfile, 'tiktok_username' | 'first_name' | 'last_name' | 'email' | 'phone'>>) =>
  API.patch<UserProfile>('/auth/profile', data);

// Réglage rappel SMS avant match (par streamer) — voir CODEX §30
export interface SmsReminder { enabled: boolean; lead_min: number; phone: string | null; }
export const getSmsReminder = (streamerId: number) =>
  API.get<SmsReminder>(`/streamers/${streamerId}/sms-reminder`);
export const setSmsReminder = (streamerId: number, data: { enabled?: boolean; lead_min?: number }) =>
  API.patch<SmsReminder>(`/streamers/${streamerId}/sms-reminder`, data);
export const uploadAvatar = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ status: string; avatar_url: string }>('/auth/avatar', fd);
};

export interface StreamerAccount {
  display_name: string;
  account: UserProfile | null;
}

// Teams
export interface TeamMember {
  id: number; username: string; display_name: string; avatar_url?: string | null;
  diamonds?: number | null; live_duration_minutes?: number | null; live_days?: number | null;
  tiktok_live?: boolean;
  statut_echelon?: string | null;
  remu_niveau?: number | null;
  remu_activite?: number | null;
  remu_progressif?: number | null;
  remu_total?: number | null;
  team_niveau?: number | null;
  team_activite?: number | null;
  team_total?: number | null;
  leader_role?: string | null;
}
export interface Team {
  groupe: string; period?: string | null;
  total_diamonds: number; total_duration: number; total_days: number;
  total_lives: number; member_count: number;
  total_remu?: number | null;
  total_remu_niveau?: number | null;
  total_remu_activite?: number | null;
  total_remu_progressif?: number | null;
  members: TeamMember[];
  remu_enabled?: boolean;
  niveau_rates?: NiveauRates;
  activite_rates?: (number | null)[];
  team_remu_niveau?: number | null;
  team_remu_activite?: number | null;
  team_total_remu?: number | null;
}
export const getTeams = (period?: string) =>
  API.get<Team[]>('/teams', { params: period ? { period } : {} });
export const getPeriods = () => API.get<string[]>('/stats/periods');

export interface TeamLeader {
  streamer_id: number;
  display_name: string;
  username: string;
  avatar_url?: string | null;
  role: 'Agent' | 'Co-Leader' | 'Recruteur';
}

export const getTeamLeaders = (groupe: string) =>
  API.get<TeamLeader[]>(`/teams/leaders/${encodeURIComponent(groupe)}`);
export const addTeamLeader = (groupe: string, streamer_id: number, role: string) =>
  API.post(`/teams/leaders/${encodeURIComponent(groupe)}`, { streamer_id, role });
export const updateTeamLeaderRole = (groupe: string, streamer_id: number, role: string) =>
  API.patch(`/teams/leaders/${encodeURIComponent(groupe)}/${streamer_id}`, { streamer_id, role });
export const removeTeamLeader = (groupe: string, streamer_id: number) =>
  API.delete(`/teams/leaders/${encodeURIComponent(groupe)}/${streamer_id}`);

export interface NiveauRates {
  montee: (number | null)[];
  maintien: (number | null)[];
  non_maintenu: (number | null)[];
}

export interface TeamRemu {
  groupe: string;
  enabled: boolean | number;
  niveau_rates: NiveauRates;
  activite_rates: (number | null)[];
  period?: string | null;
  total_team_niveau?: number;
  total_team_activite?: number;
  total_team_remu?: number;
  members?: TeamRemuMember[];
}

export interface TeamRemuMember {
  id: number;
  display_name: string;
  username: string;
  avatar_url?: string | null;
  tiktok_live?: string | null;
  diamonds?: number | null;
  live_days?: number | null;
  live_duration_minutes?: number | null;
  statut_echelon?: string | null;
  remu_niveau?: number | null;
  remu_activite?: number | null;
  team_niveau?: number | null;
  team_activite?: number | null;
  team_total?: number | null;
}

export const getTeamRemu = (groupe: string, period?: string) =>
  API.get<TeamRemu>(`/teams/${encodeURIComponent(groupe)}/remu`, { params: period ? { period } : {} });

export const updateTeamRemu = (groupe: string, data: { enabled: boolean; niveau_rates: NiveauRates; activite_rates: (number | null)[] }) =>
  API.patch(`/teams/${encodeURIComponent(groupe)}/remu`, data);

// Streamers
export const getStreamers = (period?: string) =>
  API.get<Streamer[]>('/streamers', { params: period ? { period } : {} });
export const getLiveStreamers = () => API.get<LiveStreamer[]>('/streamers/live');
export const getFollowSuggestions = () =>
  API.get<FollowSuggestion[]>('/dashboard/follow-suggestions');
export const confirmFollowSuggestion = (target_streamer_id: number) =>
  API.post(`/dashboard/follow-suggestions/${target_streamer_id}/confirm`);
export const unconfirmFollowSuggestion = (target_streamer_id: number) =>
  API.delete(`/dashboard/follow-suggestions/${target_streamer_id}/confirm`);
export const getLiveSessions = (streamer_id: number, month?: string) =>
  API.get<LiveSession[]>(`/streamers/${streamer_id}/live/sessions`, { params: month ? { month } : {} });
export const getLiveDonors = (session_id: number) =>
  API.get<LiveDonor[]>(`/live-sessions/${session_id}/donors`);
export const getLiveFollowers = (session_id: number) =>
  API.get<LiveFollower[]>(`/live-sessions/${session_id}/followers`);
export const getTopLiveDonors = (streamer_id: number, month?: string, limit = 10) =>
  API.get<TopLiveDonor[]>(`/streamers/${streamer_id}/live/top-donors`, { params: { ...(month ? { month } : {}), limit } });
export const createStreamer = (username: string, display_name: string) =>
  API.post('/streamers', { username, display_name });
export const deleteStreamer = (id: number) => API.delete(`/streamers/${id}`);

// Compte streamer
export const getStreamerAccount = (id: number) =>
  API.get<StreamerAccount>(`/streamers/${id}/account`);
export const updateStreamerAccount = (id: number, data: {
  display_name?: string; tiktok_username?: string | null;
  first_name?: string | null; last_name?: string | null; email?: string | null; phone?: string | null;
}) => API.patch<StreamerAccount>(`/streamers/${id}/account`, data);
export const uploadStreamerAccountAvatar = (id: number, file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return API.post<{ status: string; avatar_url: string }>(`/streamers/${id}/account/avatar`, fd);
};
export const changeStreamerAccountPassword = (id: number, current_password: string, new_password: string) =>
  API.patch(`/streamers/${id}/account/password`, { current_password, new_password });

// Règles (ancien système)
export const getRules = (streamer_id: number) =>
  API.get<Rule[]>(`/streamers/${streamer_id}/rules`);
export const createRule = (streamer_id: number, gift_name: string, animation_file: string, tiktok_gift_id?: number | null) =>
  API.post(`/streamers/${streamer_id}/rules`, { gift_name, animation_file, tiktok_gift_id });
export const deleteRule = (rule_id: number, delete_file = false) =>
  API.delete(`/rules/${rule_id}${delete_file ? '?delete_file=true' : ''}`);
export const updateRule = (rule_id: number, gift_name: string, animation_file: string) =>
  API.patch(`/rules/${rule_id}`, { gift_name, animation_file });

// Animations
export const getAnimations = () => API.get<{ animations: string[] }>('/animations');
export const uploadAnimation = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ status: string; filename: string; converted?: boolean; message?: string | null }>('/animations/upload', fd);
};
export const deleteAnimation = (filename: string) =>
  API.delete(`/animations/${encodeURIComponent(filename)}`);

// Fichiers animation liés à un thème spécifique
export const getThemeAnimationFiles = (theme_id: number) =>
  API.get<{ animations: string[] }>(`/themes/${theme_id}/animation-files`);
export const uploadThemeAnimationFile = (theme_id: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ status: string; filename: string; converted?: boolean; message?: string | null }>(
    `/themes/${theme_id}/animation-files/upload`, fd
  );
};

// Preview
export const previewAnimation = (
  streamer_id: number, animation_file: string,
  pos_x: number, pos_y: number, pos_w: number, pos_h: number
) =>
  API.post(`/streamers/${streamer_id}/preview`, { animation_file, pos_x, pos_y, pos_w, pos_h });

// Position
export const updateRulePosition = (
  rule_id: number, pos_x: number, pos_y: number, pos_w: number, pos_h: number
) => API.patch(`/rules/${rule_id}/position`, { pos_x, pos_y, pos_w, pos_h });

// Animation file URL (public)
export const animationFileUrl = (filename: string) =>
  `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/animation-file/${encodeURIComponent(filename)}`;

// Overlay image URL (public)
export const overlayUrl = (filename: string) =>
  `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/overlays/${encodeURIComponent(filename)}`;

// Overlay
export const uploadOverlay = (streamer_id: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post(`/streamers/${streamer_id}/overlay`, fd);
};
export const removeOverlay = (streamer_id: number) =>
  API.delete(`/streamers/${streamer_id}/overlay`);

// Bibliothèque de jeux
export const getGames = (hasActions = false) =>
  API.get<Game[]>(`/games${hasActions ? '?has_actions=true' : ''}`);
export const getGameActionById = (id: number) =>
  API.get<GameAction & { game_id: number; game_name: string }>(`/game-actions/${id}`);
export const updateRuleGameAction = (rule_id: number, game_action_id: number | null) =>
  API.patch(`/rules/${rule_id}/game-action`, { game_action_id });
export const getGifts = () => API.get<Gift[]>('/gifts');
export const getPublicAgency = () => API.get<{ name: string | null; logo_url: string | null }>('/public/agency');
export const translateGifts = () => API.post<{ translated: number; message?: string }>('/gifts/translate');
export const getSettings = () => API.get<Record<string, string | null>>('/settings');
export const getObjectiveSettings = () => API.get<Record<string, string | null>>('/settings/objectives');
export const setSetting = (key: string, value: string) => API.patch(`/settings/${key}`, { value });
export const uploadAgencyLogo = (file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return API.post<{ status: string; logo_url: string }>('/settings/agency-logo', fd);
};

// Liaisons cadeau → action jeu
export const getGiftGameRules = (streamer_id: number, game_id: number) =>
  API.get<GiftGameRule[]>(`/streamers/${streamer_id}/game-rules/${game_id}`);
export const createGiftGameRule = (streamer_id: number, game_id: number, gift_name: string, game_action_id: number, tiktok_gift_id?: number | null) =>
  API.post(`/streamers/${streamer_id}/game-rules/${game_id}`, { gift_name, game_action_id, tiktok_gift_id });
export const deleteGiftGameRule = (rule_id: number) =>
  API.delete(`/game-rules/${rule_id}`);
export const createGame = (name: string, slug: string, image_url: string | null, platforms?: string) =>
  API.post('/games', { name, slug, image_url, platforms });
export const updateGame = (id: number, name: string, image_url: string | null, platforms?: string) =>
  API.put(`/games/${id}`, { name, image_url, platforms });
export const getAgentMode = (streamer_id: number) =>
  API.get<{ mode: string }>(`/streamers/${streamer_id}/agent-mode`);
export const setAgentMode = (streamer_id: number, mode: string) =>
  API.post<{ mode: string }>(`/streamers/${streamer_id}/agent-mode`, { mode });
export const deleteGame = (id: number) => API.delete(`/games/${id}`);
export const getGameActions = (game_id: number) =>
  API.get<GameAction[]>(`/games/${game_id}/actions`);
export const getStreamerGameActions = (streamer_id: number, game_id: number) =>
  API.get<GameAction[]>(`/streamers/${streamer_id}/games/${game_id}/actions`);
export const createGameAction = (game_id: number, data: Omit<GameAction, 'id' | 'game_id' | 'sort_order'>) =>
  API.post(`/games/${game_id}/actions`, data);
export const updateGameAction = (id: number, data: Omit<GameAction, 'id' | 'game_id' | 'sort_order'>) =>
  API.put(`/game-actions/${id}`, data);
export const deleteGameAction = (id: number) => API.delete(`/game-actions/${id}`);
export const createStreamerGameAction = (streamer_id: number, game_id: number, data: Omit<GameAction, 'id' | 'game_id' | 'sort_order'>) =>
  API.post(`/streamers/${streamer_id}/games/${game_id}/actions`, data);
export const updateStreamerGameAction = (streamer_id: number, id: number, data: Omit<GameAction, 'id' | 'game_id' | 'sort_order'>) =>
  API.put(`/streamers/${streamer_id}/game-actions/${id}`, data);
export const deleteStreamerGameAction = (streamer_id: number, id: number) =>
  API.delete(`/streamers/${streamer_id}/game-actions/${id}`);

// Users (admin)
export const getUsers = () => API.get<AppUser[]>('/users');
export const createUser = (data: {
  username: string; password: string; role: string;
  streamer_id: number | null; permission: string;
}) => API.post('/users', data);
export const deleteUser = (id: number) => API.delete(`/users/${id}`);
export const updatePermission = (id: number, permission: string) =>
  API.patch(`/users/${id}/permission`, { permission });
export const adminResetPassword = (id: number, new_password: string) =>
  API.patch(`/users/${id}/password`, { current_password: '', new_password });
export const setModulePermissions = (id: number, perms: {
  can_manage_matchs: boolean; can_manage_events: boolean; can_manage_lives: boolean; can_manage_themes?: boolean;
}) => API.patch(`/users/${id}/module-permissions`, perms);
export const getMyAccess = () => API.get<AccessInfo>('/auth/me/access');

// ── Ressources (Infos / Tutos / Guides agents) ──────────
export interface ResourceItem {
  id: number;
  section: 'info' | 'tuto' | 'concept' | 'agent_guide';
  title: string;
  body: string | null;
  image_filename: string | null;
  image_url: string | null;
  extra: {
    images?: string[];
    icon?: string | null;
    color_theme?: string | null;
    objective?: string | null;
    frequency?: string | null;
    steps?: string[];
    best_practices?: string[];
    tips?: string[];
    avoid?: string[];
    example_message?: string | null;
    tags?: string[];
  } | null;
  sort_order: number;
  created_at?: string;
}
export const getResources = (section: string) =>
  API.get<ResourceItem[]>(`/resources?section=${section}`);
export const getResourceDetail = (id: number) =>
  API.get<ResourceItem>(`/resources/${id}`);
export const createResource = (data: {
  section: string; title: string; body?: string | null;
  extra_json?: string | null; sort_order?: number;
}) => API.post<ResourceItem>('/resources', data);
export const updateResource = (id: number, data: Partial<{
  section: string; title: string; body: string | null;
  extra_json: string | null; sort_order: number;
}>) => API.patch<ResourceItem>(`/resources/${id}`, data);
export const deleteResource = (id: number) => API.delete(`/resources/${id}`);
export const uploadResourceImage = (id: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ image_url: string; image_filename: string }>(
    `/resources/${id}/image`, fd);
};

// ── Thèmes ──────────────────────────────────────────────
export const getThemes = () => API.get<Theme[]>('/themes');
export const createTheme = (name: string, slug: string, image_url: string | null) =>
  API.post<{ id: number }>('/themes', { name, slug, image_url });
export const updateTheme = (id: number, name: string, image_url: string | null) =>
  API.put(`/themes/${id}`, { name, image_url });
export const deleteTheme = (id: number) => API.delete(`/themes/${id}`);
export const uploadThemeImage = (theme_id: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ status: string; image_url: string }>(`/themes/${theme_id}/image`, fd);
};
export const uploadThemeOverlay = (theme_id: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ status: string; filename: string }>(`/themes/${theme_id}/overlay`, fd);
};
export const removeThemeOverlay = (theme_id: number) =>
  API.delete(`/themes/${theme_id}/overlay`);

// Animations de thème
export const getThemeAnimations = (theme_id: number) =>
  API.get<ThemeAnimation[]>(`/themes/${theme_id}/animations`);
export const createThemeAnimation = (theme_id: number, data: Omit<ThemeAnimation, 'id' | 'theme_id' | 'sort_order'>) =>
  API.post<{ id: number }>(`/themes/${theme_id}/animations`, data);
export const updateThemeAnimation = (id: number, data: Omit<ThemeAnimation, 'id' | 'theme_id' | 'sort_order'>) =>
  API.put(`/theme-animations/${id}`, data);
export const deleteThemeAnimation = (id: number) => API.delete(`/theme-animations/${id}`);

// Images permanentes
export const getStaticElements = (theme_id: number) =>
  API.get<StaticElement[]>(`/themes/${theme_id}/static-elements`);
export const createStaticElement = (theme_id: number, data: Omit<StaticElement, 'id' | 'theme_id'>) =>
  API.post<{ id: number }>(`/themes/${theme_id}/static-elements`, data);
export const updateStaticElement = (id: number, data: Omit<StaticElement, 'id' | 'theme_id'>) =>
  API.put(`/theme-static-elements/${id}`, data);
export const deleteStaticElement = (id: number) => API.delete(`/theme-static-elements/${id}`);

// Texte dynamique (saisi en direct par le streamer)
export async function sendDynamicText(streamerId: number, payload: {
  text: string;
  text_color: string;
  text_size: number;
  text_font: string;
  text_effect: string;
  text_loops: number;
  text_duration: number;
  text_bg_color: string;
  text_bg_opacity: number;
  text_bg_anim: string;
  pos_x: number; pos_y: number; pos_w: number; pos_h: number;
}): Promise<void> {
  await API.post(`/streamers/${streamerId}/dynamic-text`, payload);
}

// Streamers ↔ thèmes
export const getThemeStreamers = (theme_id: number) =>
  API.get<ThemeStreamer[]>(`/themes/${theme_id}/streamers`);
export const getStreamerThemes = (streamer_id: number) =>
  API.get<StreamerTheme[]>(`/streamers/${streamer_id}/themes`);
export const assignTheme = (streamer_id: number, theme_id: number) =>
  API.post(`/streamers/${streamer_id}/themes/${theme_id}`, {});
export const unassignTheme = (streamer_id: number, theme_id: number) =>
  API.delete(`/streamers/${streamer_id}/themes/${theme_id}`);
export const setActiveTheme = (streamer_id: number, theme_id: number | null) =>
  API.patch(`/streamers/${streamer_id}/active-theme`, { theme_id });
export const setActiveGame = (streamer_id: number, game_id: number | null) =>
  API.patch(`/streamers/${streamer_id}/active-game`, { game_id });

// Assignations cadeau → animation
export const getAnimationAssignments = (streamer_id: number, theme_id: number) =>
  API.get<AnimationAssignment[]>(`/streamers/${streamer_id}/animation-assignments?theme_id=${theme_id}`);
export const upsertAnimationAssignment = (streamer_id: number, theme_animation_id: number, gift_name: string, tiktok_gift_id?: number | null) =>
  API.post(`/streamers/${streamer_id}/animation-assignments`, { theme_animation_id, gift_name, tiktok_gift_id });
export const deleteAnimationAssignment = (assignment_id: number) =>
  API.delete(`/animation-assignments/${assignment_id}`);
export const setPriorityGift = (streamer_id: number, theme_animation_id: number, gift_name: string | null, tiktok_gift_id?: number | null) =>
  API.post(`/streamers/${streamer_id}/priority-gift`, { theme_animation_id, gift_name, tiktok_gift_id });
export const updateBannerConfig = (streamer_id: number, theme_animation_id: number, enabled: boolean, config: any) =>
  API.post(`/streamers/${streamer_id}/banner-config`, { theme_animation_id, enabled, config });

// Mobile overlay / TikTok Live
export interface LiveStatus {
  running: boolean;
  status: 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected';
  error?: string | null;
  overlay_url?: string | null;
  display_name?: string;
  username?: string;
  active_theme_id?: number | null;
  dynamic_text_configs?: Array<{
    id: number;
    pos_x: number; pos_y: number; pos_w: number; pos_h: number;
    text_color: string; text_size: number; text_effect: string; text_duration: number;
    text_font?: string; text_loops?: number;
    text_bg_color?: string; text_bg_opacity?: number; text_bg_anim?: string;
  }>;
  manual_animations?: ManualAnimation[];
  banners?: Array<{
    theme_animation_id: number;
    name?: string;
    pos_x: number; pos_y: number; pos_w: number; pos_h: number;
    sort_order?: number;
    config?: Record<string, any>;
  }>;
}
export const getLiveStatus = (streamer_id: number) =>
  API.get<LiveStatus>(`/streamers/${streamer_id}/live/status`);
export const startLive = (streamer_id: number) =>
  API.post<{ status: string }>(`/streamers/${streamer_id}/live/start`);
export const stopLive = (streamer_id: number) =>
  API.post<{ status: string }>(`/streamers/${streamer_id}/live/stop`);
export const importExcel = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ imported: number; created: number; updated: number; skipped: number; deleted: number; period: string }>(
    '/streamers/import-excel', fd
  );
};

export const syncTikTokProfile = (streamer_id: number) =>
  API.post<{ ok: boolean; display_name: string | null; avatar_url: string | null }>(
    `/streamers/${streamer_id}/sync-tiktok`
  );

export interface StreamerDebugLogLine { source: string; text: string }
export const getStreamerDebugLogs = (streamer_id: number, limit: number = 200) =>
  API.get<{ username: string; lines: StreamerDebugLogLine[] }>(
    `/streamers/${streamer_id}/debug-logs`, { params: { limit } }
  );

export interface QuestionQueueKeywords {
  keywords: string[];
  noise_words: string[];
  defaults: { keywords: string[]; noise_words: string[] };
}
export const getQuestionQueueKeywords = () =>
  API.get<QuestionQueueKeywords>('/question-queue/keywords');
export const updateQuestionQueueKeywords = (keywords: string[], noise_words: string[] | null) =>
  API.put<QuestionQueueKeywords>('/question-queue/keywords', { keywords, noise_words });

// ── ElevenLabs ──
export interface ElevenLabsStatus { configured: boolean; masked: string | null }
export interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  category?: string | null;
  labels?: Record<string, string>;
  preview_url?: string | null;
  description?: string | null;
}
export const getElevenLabsStatus = () => API.get<ElevenLabsStatus>('/elevenlabs/status');
export const setElevenLabsKey = (api_key: string) =>
  API.put<ElevenLabsStatus>('/elevenlabs/api-key', { api_key });
export const getElevenLabsVoices = () => API.get<{ voices: ElevenLabsVoice[] }>('/elevenlabs/voices');
export const ttsElevenLabs = (voice_id: string, text: string) =>
  API.post('/elevenlabs/tts', { voice_id, text }, { responseType: 'blob' });

export interface QueueTTSConfig { enabled: boolean; voice_id: string }
export const getStreamerQueueTTS = (sid: number) =>
  API.get<QueueTTSConfig>(`/streamers/${sid}/queue-tts`);
export const setStreamerQueueTTS = (sid: number, enabled: boolean, voice_id: string | null) =>
  API.put<QueueTTSConfig>(`/streamers/${sid}/queue-tts`, { enabled, voice_id });

// ── Pollinations (génération de fond IA pour affiches de match) ──
export interface PollinationsStatus { configured: boolean; masked: string | null }
export interface PollinationsStyle { key: string; label: string }
export interface MatchAiImage { id: number; prompt: string; style: string; image_url: string; created_at: string }
export const getPollinationsStatus = () => API.get<PollinationsStatus>('/pollinations/status');
export const setPollinationsKey = (api_key: string) =>
  API.put<PollinationsStatus>('/pollinations/api-key', { api_key });
export const getPollinationsStyles = () => API.get<{ styles: PollinationsStyle[] }>('/pollinations/styles');
export const getMatchAiImages = (matchId: number) =>
  API.get<{ images: MatchAiImage[]; is_admin: boolean; limit: number | null; used: number }>(`/matches/${matchId}/ai-images`);
export const generateMatchBackground = (matchId: number, prompt: string, style: string) =>
  API.post<{ status: string; filename: string; image_url: string; is_admin: boolean; limit: number | null; used: number }>(
    `/matches/${matchId}/generate-background`, { prompt, style });
export const setMatchBackgroundFilename = (matchId: number, filename: string) =>
  API.post<{ status: string; background_filename: string }>(`/matches/${matchId}/set-background-filename`, { filename });
export const uploadMatchBackground = (matchId: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ status: string; filename: string; background_filename: string; image_url: string }>(
    `/matches/${matchId}/upload-background`, fd);
};

export async function triggerManualAnimation(streamerId: number, animId: number, volume: number = 1.0): Promise<void> {
  await API.post(`/streamers/${streamerId}/trigger-manual/${animId}?volume=${volume}`);
}

export interface QuestionItem {
  id: number;
  streamer_id: number;
  username: string;
  nickname?: string | null;
  avatar_url?: string | null;
  question: string;
  coins: number;
  gift_name?: string | null;
  status: 'waiting' | 'active' | 'done' | 'skipped';
  priority: number;
  created_at: string;
}
export interface QuestionQueueState {
  action: string;
  enabled: boolean;
  config: Record<string, any>;
  items: QuestionItem[];
  count: number;
  active: QuestionItem | null;
  paused: boolean;
}
export const getQuestionQueue = (sid: number) =>
  API.get<QuestionQueueState>(`/streamers/${sid}/question-queue`);
export const queueShow = (sid: number, id?: number) =>
  API.post(`/streamers/${sid}/question-queue/show`, { id: id ?? null });
export const queueNext = (sid: number) =>
  API.post(`/streamers/${sid}/question-queue/next`);
export const queueSkip = (sid: number, qid: number) =>
  API.post(`/streamers/${sid}/question-queue/${qid}/skip`);
export const queueDone = (sid: number, qid: number) =>
  API.post(`/streamers/${sid}/question-queue/${qid}/done`);
export const queuePriority = (sid: number, qid: number) =>
  API.post(`/streamers/${sid}/question-queue/${qid}/priority`);
export const queueClear = (sid: number) =>
  API.post(`/streamers/${sid}/question-queue/clear`);
export const queuePause = (sid: number, paused: boolean) =>
  API.post(`/streamers/${sid}/question-queue/pause`, { paused });
export const queueTest = (sid: number) =>
  API.post(`/streamers/${sid}/question-queue/test`);

export interface RaffleEntry {
  id: number;
  contest_id: number;
  username: string;
  nickname?: string | null;
  avatar_url?: string | null;
  gift_name?: string | null;
  gift_image_url?: string | null;
  entries_count: number;
  coins: number;
  last_seen_at?: string | null;
}

export interface RaffleState {
  contest: Record<string, any>;
  config: AnimationAssignment;
  entries: RaffleEntry[];
  total_entries: number;
  total_participants: number;
}

export const getRaffles = (sid: number) =>
  API.get<RaffleState[]>(`/streamers/${sid}/raffles`);
export const drawRaffle = (sid: number, theme_animation_id: number, mode: 'gift_random' | 'top_donor' = 'gift_random') =>
  API.post(`/streamers/${sid}/raffles/draw`, { theme_animation_id, mode });
export const resetRaffle = (sid: number, theme_animation_id: number) =>
  API.post(`/streamers/${sid}/raffles/reset`, { theme_animation_id });

export const setAnimationVolume = (themeAnimationId: number, volume: number) =>
  API.patch(`/theme-animations/${themeAnimationId}/volume`, { volume });

export const refreshOverlay = (streamerId: number) =>
  API.post<{ status: string; pc_connected: boolean }>(`/streamers/${streamerId}/overlay/refresh`);

export const previewAnimationMobile = (
  streamer_id: number, animation_file: string,
  pos_x: number, pos_y: number, pos_w: number, pos_h: number
) =>
  API.post(`/streamers/${streamer_id}/preview-mobile`, { animation_file, pos_x, pos_y, pos_w, pos_h });

// ── Lives Matchs ─────────────────────────────────────────────
export interface LiveMatch {
  id: number;
  match_type: string;
  match_label?: string | null;
  scheduled_at: string;
  streamer1_id: number;
  streamer1_name?: string | null;
  streamer1_avatar?: string | null;
  streamer1_username?: string | null;
  streamer2_id?: number | null;
  streamer2_name: string;
  streamer2_avatar_url?: string | null;
  streamer2_username?: string | null;
  score1?: number | null;
  score2?: number | null;
  forfeit?: string | null;   // 'streamer1' | 'streamer2' | 'none' | null
  poster_filename?: string | null;
  poster_url?: string | null;
  background_filename?: string | null;
  status: 'scheduled' | 'completed';
  created_at: string;
}

export const getMatches = () =>
  API.get<LiveMatch[]>('/matches');

export const createMatch = (data: {
  match_type: string;
  match_label?: string | null;
  scheduled_at: string;
  streamer1_id: number;
  streamer2_id?: number | null;
  streamer2_name: string;
  streamer2_avatar_url?: string | null;
  streamer2_username?: string | null;
}) => API.post<LiveMatch>('/matches', data);

export const updateMatch = (id: number, data: {
  score1?: number | null;
  score2?: number | null;
  status?: string;
  match_type?: string;
  match_label?: string | null;
  scheduled_at?: string;
  streamer1_id?: number;
  streamer2_id?: number | null;
  streamer2_name?: string;
  streamer2_avatar_url?: string | null;
  streamer2_username?: string | null;
  forfeit?: string;
}) => API.patch<LiveMatch>(`/matches/${id}`, data);

export const deleteMatch = (id: number) =>
  API.delete(`/matches/${id}`);

export interface MatchRequest {
  id: number;
  requester_id: number;
  requester_name?: string | null;
  requester_avatar?: string | null;
  match_type: string;
  match_label?: string | null;
  scheduled_at: string;
  opponent_id?: number | null;
  opponent_name?: string | null;
  opponent_avatar?: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  admin_notes?: string | null;
  created_at: string;
}

export const getMatchRequests = () =>
  API.get<MatchRequest[]>('/match-requests');

export const createMatchRequest = (data: {
  match_type: string;
  match_label?: string | null;
  scheduled_at: string;
  opponent_id?: number | null;
}) => API.post<MatchRequest>('/match-requests', data);

export const updateMatchRequest = (id: number, data: {
  status?: string;
  admin_notes?: string | null;
  opponent_id?: number | null;
  match_type?: string;
  match_label?: string | null;
  scheduled_at?: string;
}) => API.patch<MatchRequest>(`/match-requests/${id}`, data);

export const acceptMatchRequest = (id: number) =>
  API.post<LiveMatch>(`/match-requests/${id}/accept`);

export const deleteMatchRequest = (id: number) =>
  API.delete(`/match-requests/${id}`);

// ── Demandes d'événement (liver → admin) ────────────────
export interface EventRequest {
  id: number;
  requester_id: number;
  requester_name?: string | null;
  requester_avatar?: string | null;
  title: string;
  description?: string | null;
  event_type: 'live' | 'concours';
  desired_schedule?: string | null;
  desired_prizes?: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  admin_notes?: string | null;
  created_at: string;
}
export const getEventRequests = () =>
  API.get<EventRequest[]>('/event-requests');
export const createEventRequest = (data: {
  title: string;
  description?: string | null;
  event_type: string;
  desired_schedule?: string | null;
  desired_prizes?: string | null;
}) => API.post<EventRequest>('/event-requests', data);
export const updateEventRequest = (id: number, data: { status?: string; admin_notes?: string | null }) =>
  API.patch<EventRequest>(`/event-requests/${id}`, data);
export const acceptEventRequest = (id: number) =>
  API.post<LiveEvent>(`/event-requests/${id}/accept`);
export const deleteEventRequest = (id: number) =>
  API.delete(`/event-requests/${id}`);

// ── Événements Live ──────────────────────────────────────────

export interface EventStreamer {
  streamer_id: number;
  display_name: string;
  avatar_url?: string | null;
}

export interface EventRecurrence {
  id: number;
  event_id: number;
  recurrence_type: 'once' | 'weekly';
  day_of_week?: number | null;
  time_start: string;
  time_end: string;
  date_from: string;
  date_until?: string | null;
  weeks_count?: number | null;
}

export interface EventOccurrence {
  id: number;
  event_id: number;
  recurrence_id?: number | null;
  occurrence_date: string;
  time_start: string;
  time_end: string;
  image_filename?: string | null;
  image_url?: string | null;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  notes?: string | null;
  participants_locked?: number;
  created_at: string;
  recurrence_type?: string | null;
  day_of_week?: number | null;
}

export interface EventFormField {
  id: number;
  event_id: number;
  sort_order: number;
  field_type: 'text' | 'textarea' | 'checkbox' | 'radio' | 'select' | 'file' | 'section' | 'tiktok';
  label: string;
  required: number;
  options_json?: string | null;
  dropbox_folder?: string | null;
  placeholder?: string | null;
  default_value?: string | null;
}

export interface EventFormTemplate {
  id: number;
  name: string;
  fields_json: string;
  created_at: string;
}

export interface EventApplication {
  id: number;
  event_id: number;
  applicant_name: string;
  applicant_tiktok?: string | null;
  applicant_email?: string | null;
  created_at: string;
  overall_status: 'pending' | 'active' | 'eliminated' | 'finalist' | 'winner';
  answers?: Array<{ id: number; field_id: number; label?: string; field_type?: string; field_options?: string | null; answer_text?: string | null; file_url?: string | null }>;
  tk_name?: string | null;
  tk_avatar?: string | null;
}

export interface EventParticipant {
  id: number;
  occurrence_id: number;
  application_id: number;
  score?: number | null;
  notes?: string | null;
  status: 'competing' | 'eliminated' | 'finalist' | 'winner';
  applicant_name: string;
  applicant_tiktok?: string | null;
  applicant_email?: string | null;
  created_at?: string | null;
  tk_name?: string | null;
  tk_avatar?: string | null;
  answers?: Array<{ id: number; field_id: number; label?: string; field_type?: string; field_options?: string | null; answer_text?: string | null; file_url?: string | null }>;
  pass_order?: number | null;
  avg_score?: number | null;
  performances?: Array<{ id: number; score: number | null; notes: string | null }>;
}

export interface LiveEvent {
  id: number;
  title: string;
  description?: string | null;
  event_type: 'live' | 'concours';
  image_filename?: string | null;
  image_url?: string | null;
  created_at: string;
  streamers?: EventStreamer[];
  streamer_ids?: number[];
  recurrences?: EventRecurrence[];
  rules_json?: string | null;
  prizes_json?: string | null;
  min_participants?: number | null;
  official_start_date?: string | null;
  recurrence_summary?: string;
  selected_count?: number;
  pending_start?: boolean;
  launched?: boolean;
  winners?: Array<{ application_id: number; name: string; avatar_url?: string | null; tiktok?: string | null }>;
}

export interface EventLaunchStatus {
  enabled: boolean;
  min_participants: number;
  selected_count: number;
  launched: boolean;
  official_start_date: string | null;
  ready: boolean;
  proposed_start_date: string | null;
  can_manage: boolean;
}

export const getEvents = () => API.get<LiveEvent[]>('/events');
export const createEvent = (data: { title: string; description?: string | null; event_type: string; streamer_ids?: number[] }) =>
  API.post<LiveEvent>('/events', data);
export const getEvent = (id: number) => API.get<LiveEvent>(`/events/${id}`);
export const updateEvent = (id: number, data: { title?: string; description?: string | null; event_type?: string; streamer_ids?: number[]; rules_json?: string | null; prizes_json?: string | null; min_participants?: number | null; official_start_date?: string | null }) =>
  API.patch<LiveEvent>(`/events/${id}`, data);
export const deleteEvent = (id: number) => API.delete(`/events/${id}`);
export const uploadEventImage = (id: number, file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return API.post<{ image_url: string; image_filename: string }>(`/events/${id}/image`, fd);
};
export const uploadOccurrenceImage = (eventId: number, occId: number, file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return API.post<{ image_url: string; image_filename: string }>(`/events/${eventId}/occurrences/${occId}/image`, fd);
};

export const addEventRecurrence = (eventId: number, data: Omit<EventRecurrence, 'id' | 'event_id'>) =>
  API.post<EventRecurrence>(`/events/${eventId}/recurrences`, data);
export const deleteEventRecurrence = (eventId: number, recId: number) =>
  API.delete(`/events/${eventId}/recurrences/${recId}`);
export const generateOccurrences = (eventId: number, recId: number) =>
  API.post<{ generated: number; occurrence_ids: number[] }>(`/events/${eventId}/recurrences/${recId}/generate`);

export const getEventOccurrences = (eventId: number) =>
  API.get<EventOccurrence[]>(`/events/${eventId}/occurrences`);

export const deleteEventOccurrence = (eventId: number, occId: number) =>
  API.delete(`/events/${eventId}/occurrences/${occId}`);
export const clearEventOccurrences = (eventId: number) =>
  API.delete<{ status: string; deleted: number }>(`/events/${eventId}/occurrences`);

export const getEventLaunchStatus = (eventId: number) =>
  API.get<EventLaunchStatus>(`/events/${eventId}/launch-status`);
export const launchEvent = (eventId: number, start_date: string) =>
  API.post<{ ok: boolean; official_start_date: string; generated: number }>(
    `/events/${eventId}/launch`, { start_date });
export const updateEventOccurrence = (eventId: number, occId: number, data: { status?: string; notes?: string | null; time_start?: string; time_end?: string }) =>
  API.patch<EventOccurrence>(`/events/${eventId}/occurrences/${occId}`, data);

export const getEventFormFields = (eventId: number) =>
  API.get<EventFormField[]>(`/events/${eventId}/form-fields`);
export const addEventFormField = (eventId: number, data: { field_type: string; label: string; required: boolean; options_json?: string | null; dropbox_folder?: string | null; placeholder?: string | null; default_value?: string | null }) =>
  API.post<EventFormField>(`/events/${eventId}/form-fields`, data);
export const updateEventFormField = (eventId: number, fieldId: number, data: Partial<EventFormField>) =>
  API.patch<EventFormField>(`/events/${eventId}/form-fields/${fieldId}`, data);
export const deleteEventFormField = (eventId: number, fieldId: number) =>
  API.delete(`/events/${eventId}/form-fields/${fieldId}`);
export const reorderEventFormFields = (eventId: number, ordered_ids: number[]) =>
  API.put<EventFormField[]>(`/events/${eventId}/form-fields/reorder`, { ordered_ids });

// Modèles de formulaire réutilisables
export const getEventFormTemplates = () =>
  API.get<EventFormTemplate[]>('/event-form-templates');
export const saveEventFormTemplate = (name: string, event_id: number) =>
  API.post<{ id: number; name: string; count: number }>('/event-form-templates', { name, event_id });
export const applyEventFormTemplate = (eventId: number, templateId: number, replace = true) =>
  API.post<EventFormField[]>(`/events/${eventId}/apply-template/${templateId}`, { replace });
export const deleteEventFormTemplate = (templateId: number) =>
  API.delete(`/event-form-templates/${templateId}`);

// Résolution profil TikTok (public)
export const resolveTikTokProfile = (username: string) =>
  API.get<{ ok: boolean; username: string; display_name: string; avatar_url: string | null }>(
    `/public/tiktok-profile?username=${encodeURIComponent(username)}`);

export const getEventApplications = (eventId: number) =>
  API.get<EventApplication[]>(`/events/${eventId}/applications`);
export const getEventApplication = (eventId: number, appId: number) =>
  API.get<EventApplication>(`/events/${eventId}/applications/${appId}`);
export const createEventApplication = (eventId: number, data: { applicant_name: string; applicant_tiktok?: string | null; applicant_email?: string | null; answers?: Array<{ field_id: number; answer_text?: string }> }) =>
  API.post<EventApplication>(`/events/${eventId}/applications`, data);
export const updateEventApplication = (eventId: number, appId: number, data: { overall_status: string }) =>
  API.patch<EventApplication>(`/events/${eventId}/applications/${appId}`, data);

export const getEventApplicationsFull = (eventId: number) =>
  API.get<EventApplication[]>(`/events/${eventId}/applications-full`);
export const getOccurrenceParticipantsFull = (eventId: number, occId: number) =>
  API.get<EventParticipant[]>(`/events/${eventId}/occurrences/${occId}/participants-full`);

export const getOccurrenceParticipants = (eventId: number, occId: number) =>
  API.get<EventParticipant[]>(`/events/${eventId}/occurrences/${occId}/participants`);
export const addOccurrenceParticipants = (eventId: number, occId: number, application_ids: number[]) =>
  API.post<EventParticipant[]>(`/events/${eventId}/occurrences/${occId}/participants`, { application_ids });
export const updateOccurrenceParticipant = (eventId: number, occId: number, participantId: number, data: { score?: number | null; notes?: string | null; status?: string; pass_order?: number | null }) =>
  API.patch<EventParticipant[]>(`/events/${eventId}/occurrences/${occId}/participants/${participantId}`, data);
export const addParticipantPerformance = (eventId: number, occId: number, pid: number, data: { score?: number | null; notes?: string | null }) =>
  API.post<{ status: string; id: number }>(`/events/${eventId}/occurrences/${occId}/participants/${pid}/performances`, data);
export const updateParticipantPerformance = (eventId: number, occId: number, pid: number, perfId: number, data: { score?: number | null; notes?: string | null }) =>
  API.patch(`/events/${eventId}/occurrences/${occId}/participants/${pid}/performances/${perfId}`, data);
export const deleteParticipantPerformance = (eventId: number, occId: number, pid: number, perfId: number) =>
  API.delete(`/events/${eventId}/occurrences/${occId}/participants/${pid}/performances/${perfId}`);
export const advanceWinners = (eventId: number, occId: number) =>
  API.post<{ advanced: number; winners: number; finalists: number }>(`/events/${eventId}/occurrences/${occId}/advance`);
export const removeOccurrenceParticipant = (eventId: number, occId: number, participantId: number) =>
  API.delete(`/events/${eventId}/occurrences/${occId}/participants/${participantId}`);
export const deleteEventApplication = (eventId: number, appId: number) =>
  API.delete(`/events/${eventId}/applications/${appId}`);
export const setSessionLock = (eventId: number, occId: number, locked: boolean) =>
  API.patch(`/events/${eventId}/occurrences/${occId}/lock`, { locked });

export const testDropboxConnection = () => API.get<{ ok: boolean; account: string; email: string; mode?: string; warning?: string | null }>('/settings/test-dropbox');
export const getDropboxAuthUrl = () => API.get<{ url: string }>('/settings/dropbox/auth-url');
export const dropboxExchangeCode = (code: string) => API.post<{ ok: boolean; account: string }>('/settings/dropbox/exchange-code', { code });

export interface MaintenanceActionInfo {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  script_path: string;
  mode: 'wait' | 'detached';
  disabled_reason?: string | null;
}

export interface MaintenanceStatus {
  hostname: string;
  ip: string;
  role: 'development' | 'production' | 'unknown';
  label: string;
  is_dev: boolean;
  is_prod: boolean;
  local?: MaintenanceServerMetrics;
  prod?: MaintenanceServerMetrics & { ok?: boolean; error?: string } | null;
  sync?: {
    checked: boolean;
    up_to_date?: boolean;
    changes_count?: number;
    changes_sample?: string[];
    reason?: string;
  };
  actions: Record<string, MaintenanceActionInfo>;
}

export interface MaintenanceServerMetrics {
  hostname: string;
  ip: string;
  role: string;
  label: string;
  disk: {
    bytes: number;
    used_bytes: number;
    free_bytes: number;
    used_percent: number;
  };
  database: {
    path: string;
    exists: boolean;
    size_bytes: number;
    modified_at?: string | null;
  };
}

export interface MaintenanceRunResult {
  ok: boolean;
  started: boolean;
  action: string;
  message?: string;
  output?: string;
  returncode?: number;
}

export const getMaintenanceStatus = () => API.get<MaintenanceStatus>('/maintenance/status');
export const runMaintenanceAction = (actionId: string) =>
  API.post<MaintenanceRunResult>(`/maintenance/actions/${encodeURIComponent(actionId)}`);

export interface ConnectionLog {
  id: number;
  user_id: number | null;
  username: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}
export const getConnectionLogs = (limit = 300) =>
  API.get<ConnectionLog[]>(`/connection-logs?limit=${limit}`);

export interface AgentCredentials {
  streamer_id: number;
  display_name: string | null;
  token: string;
  ws_url: string;
  pc_connected: boolean;
}
export const getAgentCredentials = (streamerId: number) =>
  API.get<AgentCredentials>(`/streamers/${streamerId}/agent-credentials`);
export const regenerateAgentToken = (streamerId: number) =>
  API.post<{ ok: boolean; token: string }>(`/streamers/${streamerId}/agent-credentials/regenerate`);
export const agentPackageUrl = (streamerId: number, token: string) =>
  `${API.defaults.baseURL || ''}/streamers/${streamerId}/agent-package?token=${encodeURIComponent(token)}`;

export const uploadMatchPoster = (id: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ poster_filename: string }>(`/matches/${id}/poster`, fd);
};

export const fetchTikTokProfile = (username: string) =>
  API.post<{ display_name: string; avatar_url: string | null }>(
    `/matches/fetch-tiktok-profile?username=${encodeURIComponent(username)}`
  );

export const getMatchPosterUrl = (filename: string) =>
  `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/match-posters/${encodeURIComponent(filename)}`;

// ── Éditeur d'affiche (restauré) ───────────────────────────────────────────
export interface PosterElement {
  id: string;
  type: 'text' | 'avatar1' | 'avatar2' | 'logo' | 'image';
  x: number;          // 1080×1920 coordinates
  y: number;
  width: number;
  height: number;
  content?: string;
  font_family?: string;
  font_size?: number;
  bold?: boolean;
  italic?: boolean;
  color?: string;     // hex '#rrggbb'
  opacity?: number;   // 0–1
  text_align?: 'left' | 'center' | 'right';
  shadow_color?: string;
  shadow_offset?: number;
  border_color?: string;
  border_width?: number;
  image_url?: string;
  rotate?: number;
  logo_variant?: 'noir' | 'blanc';   // pour le logo NS Live
  grayscale?: boolean;               // filtre noir & blanc (image / VS)
  role?: 'name1' | 'name2' | 'date' | 'time' | 'matchtype';  // contenu dynamique par match
  hidden?: boolean;   // masqué sur l'affiche (case à cocher colonne de gauche)
}

export const getMatchElements = (id: number) =>
  API.get<PosterElement[]>(`/matches/${id}/elements`);

export const saveMatchElements = (id: number, elements: PosterElement[]) =>
  API.put(`/matches/${id}/elements`, { elements });

export const renderMatchPoster = (id: number, elements: PosterElement[]) =>
  API.post<{ poster_filename: string }>(`/matches/${id}/render-poster`, { elements });

export const uploadMatchEditorImage = (id: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<{ filename: string; image_url: string }>(`/matches/${id}/editor-image`, fd);
};

export const getProxyImageUrl = (url: string) =>
  `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/api/proxy-image?url=${encodeURIComponent(url)}`;

// ── Candidatures publiques (Postuler TikTokeur / Agent) ────────────────────
export interface ApplySubmission {
  id: number;
  form: string;                 // 'tiktokeur' | 'agent'
  pseudo?: string | null;
  tiktok_link?: string | null;
  concept?: string | null;
  certified_adult: number;
  is_read: number;
  created_at: string;
}
export const getApplySubmissions = () =>
  API.get<ApplySubmission[]>('/admin/apply/submissions');
export const markApplySubmission = (id: number, is_read: boolean) =>
  API.patch(`/admin/apply/submissions/${id}`, { is_read });
export const deleteApplySubmission = (id: number) =>
  API.delete(`/admin/apply/submissions/${id}`);

export interface ContactMessage {
  id: number;
  name?: string | null;
  tiktok?: string | null;
  email?: string | null;
  message: string;
  is_read: number;
  created_at: string;
}
export const getContactMessages = () =>
  API.get<ContactMessage[]>('/admin/contact/messages');
export const markContactMessage = (id: number, is_read: boolean) =>
  API.patch(`/admin/contact/messages/${id}`, { is_read });
export const deleteContactMessage = (id: number) =>
  API.delete(`/admin/contact/messages/${id}`);


export interface MobileCommentsState {
  enabled: boolean;
  tts_enabled: boolean;
  config: Record<string, any> | null;
}
export const getMobileComments = (sid: number) =>
  API.get<MobileCommentsState>(`/streamers/${sid}/mobile-comments`);
export const updateMobileComments = (sid: number, data: { enabled?: boolean; tts_enabled?: boolean }) =>
  API.post<MobileCommentsState>(`/streamers/${sid}/mobile-comments`, data);

// ── NS'Live Chat ──────────────────────────────────────────
export interface ChatPinned { id: number; content: string; media_type?: string | null; display_name?: string }
export interface ChatChannel {
  id: number; name: string; kind: 'channel' | 'group' | 'dm' | 'support'; post_policy: 'all' | 'restricted';
  dm_peer?: { user_id: number; display_name: string; avatar_url?: string | null } | null;
  guest_handle?: string | null; guest_name?: string | null; guest_avatar?: string | null;
  audience_everyone?: boolean; teams?: string[];
  notif_mode?: 'default' | 'all' | 'mentions' | 'muted';
  emoji?: string | null; member_count?: number; online?: number; unread?: number;
  last_message?: string | null; last_at?: string | null;
  pinned_message_id?: number | null; pinned_message?: ChatPinned | null;
}
export interface ChatPoll { id: number; question: string; total: number; my_vote: number | null; options: { id: number; text: string; votes: number }[] }
export interface ChatReactions { counts: Record<string, number>; mine: string[] }
export interface ChatMessage {
  id: number; channel_id: number; user_id: number; content: string; deleted?: number;
  created_at: string; display_name?: string; avatar_url?: string | null; username?: string;
  media_url?: string | null; media_type?: string | null; media_name?: string | null;
  poll?: ChatPoll | null; reactions?: ChatReactions;
}
export interface ChatMember {
  user_id: number; username: string; display_name: string; avatar_url?: string | null;
  member_role?: string; role?: string | null; online?: boolean; muted?: boolean;
}
export const getChatChannels = () => API.get<ChatChannel[]>('/chat/channels');
export const getChatTeams = () => API.get<string[]>('/chat/teams');
export interface AudienceMember { user_id: number; username: string; display_name: string; avatar_url?: string | null; groupe?: string | null }
export const getChatAudience = () => API.get<{ all: AudienceMember[]; teams: { groupe: string; members: AudienceMember[] }[] }>('/chat/audience');
export const createChatChannel = (data: { name: string; kind: string; post_policy: string; emoji?: string | null; member_ids?: number[]; everyone?: boolean; teams?: string[] }) =>
  API.post<{ id: number }>('/chat/channels', data);
export const updateChatChannel = (id: number, data: { name?: string; post_policy?: string; emoji?: string | null; everyone?: boolean; teams?: string[] }) =>
  API.patch(`/chat/channels/${id}`, data);
export const deleteChatChannel = (id: number) => API.delete(`/chat/channels/${id}`);
export const getChatMembers = (id: number) => API.get<ChatMember[]>(`/chat/channels/${id}/members`);
export const addChatMember = (id: number, user_id: number, role = 'member') =>
  API.post(`/chat/channels/${id}/members`, { user_id, role });
export const removeChatMember = (id: number, user_id: number) => API.delete(`/chat/channels/${id}/members/${user_id}`);
export const getChatMessages = (id: number, before_id?: number) =>
  API.get<ChatMessage[]>(`/chat/channels/${id}/messages${before_id ? `?before_id=${before_id}` : ''}`);
export const sendChatMessage = (id: number, content: string, media?: { media_url: string; media_type: string; media_name: string }) =>
  API.post<ChatMessage>(`/chat/channels/${id}/messages`, { content, ...(media || {}) });
export const uploadChatMedia = (id: number, file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return API.post<{ media_url: string; media_type: string; media_name: string }>(`/chat/channels/${id}/upload`, fd);
};
export const muteChatMember = (id: number, user_id: number, muted: boolean) =>
  API.post(`/chat/channels/${id}/members/${user_id}/mute`, { muted });
export const pinChatMessage = (id: number, message_id: number | null) =>
  API.post(`/chat/channels/${id}/pin`, { message_id });
export const createChatPoll = (id: number, question: string, options: string[]) =>
  API.post<ChatMessage>(`/chat/channels/${id}/poll`, { question, options });
export const voteChatPoll = (poll_id: number, option_id: number) =>
  API.post<ChatPoll>(`/chat/polls/${poll_id}/vote`, { option_id });
export const reactChatMessage = (message_id: number, emoji: string) =>
  API.post<{ counts: Record<string, number> }>(`/chat/messages/${message_id}/react`, { emoji });
export const searchChatMessages = (id: number, q: string) =>
  API.get<ChatMessage[]>(`/chat/channels/${id}/search?q=${encodeURIComponent(q)}`);
export const markChatRead = (id: number, last_id: number) =>
  API.post(`/chat/channels/${id}/read`, { last_id });
export const openChatDm = (user_id: number) =>
  API.post<{ id: number }>('/chat/dm', { user_id });
export const setChatNotifMode = (id: number, mode: 'default' | 'all' | 'mentions' | 'muted') =>
  API.post(`/chat/channels/${id}/notif`, { mode });

// ── Passerelle SMS (HAT 4G) ──
export interface SmsStatus {
  modem: boolean; port: string | null; registered: boolean; operator: string | null;
  signal: number | null; last_ok: string | null; error: string | null;
  unread: number; pending: number;
}
export interface SmsIn { id: number; sender: string; body: string; received_at: string | null; created_at: string; is_read: number }
export interface SmsOut { id: number; recipient: string; body: string; status: string; source: string | null; error: string | null; created_at: string; sent_at: string | null }
export const getSmsStatus = () => API.get<SmsStatus>('/sms/status');
export const getSmsInbox = () => API.get<SmsIn[]>('/sms/inbox');
export const markSmsRead = () => API.post('/sms/inbox/mark-read');
export const deleteSms = (id: number) => API.delete(`/sms/inbox/${id}`);
export const getSmsOutbox = () => API.get<SmsOut[]>('/sms/outbox');
export const sendSms = (to: string, text: string) => API.post<{ ok: boolean; id: number }>('/sms/send', { to, text });
export const getSmsApiKey = () => API.get<{ api_key: string }>('/sms/api-key');
export const regenSmsApiKey = () => API.post<{ api_key: string }>('/sms/api-key/regenerate');

// ── Notifications & Web Push ──
export interface NotifPrefs { [k: string]: boolean }
export const getNotifPrefs = () => API.get<{ prefs: NotifPrefs; has_push: boolean; role?: string; can_manage_matchs?: boolean; can_manage_events?: boolean; has_phone?: boolean }>('/notif/prefs');
export const setNotifPref = (key: string, enabled: boolean) => API.post('/notif/prefs', { key, enabled });
export const getVapidKey = () => API.get<{ public_key: string }>('/push/vapid-public-key');
const subscribePush = (sub: { endpoint: string; keys: any }, ua?: string) => API.post('/push/subscribe', { endpoint: sub.endpoint, keys: sub.keys, ua });
const unsubscribePush = (endpoint: string) => API.post('/push/unsubscribe', { endpoint });
export const testPush = () => API.post('/push/test');

function urlB64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(Array.from(raw, c => c.charCodeAt(0)));
}

export type PushResult = 'ok' | 'denied' | 'unsupported' | 'error';

export async function enablePushNotifications(): Promise<PushResult> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return 'denied';
    const reg = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;
    const { data } = await getVapidKey();
    // On repart toujours d'un abonnement frais (évite de réutiliser un abonnement
    // périmé / révoqué qui renverrait 410 Gone à l'envoi).
    const old = await reg.pushManager.getSubscription();
    if (old) { try { await unsubscribePush(old.endpoint); } catch { /* ignore */ } try { await old.unsubscribe(); } catch { /* ignore */ } }
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.public_key) });
    await subscribePush(sub.toJSON() as any, navigator.userAgent);
    return 'ok';
  } catch { return 'error'; }
}

export async function disablePushNotifications(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
    const sub = await reg?.pushManager.getSubscription();
    if (sub) { await unsubscribePush(sub.endpoint); await sub.unsubscribe(); }
  } catch { /* ignore */ }
}

/** Cet APPAREIL est-il abonné aux notifications ? (indépendant des autres
 *  appareils du même compte). Si un abonnement local existe, on le ré-enregistre
 *  côté serveur (idempotent) pour rester synchro. */
export async function isThisDeviceSubscribed(): Promise<boolean> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return false;
    try { await subscribePush(sub.toJSON() as any, navigator.userAgent); } catch { /* ignore */ }
    return true;
  } catch { return false; }
}
export const deleteChatMessage = (message_id: number) => API.delete(`/chat/messages/${message_id}`);
export const getChatUsers = () => API.get<ChatMember[]>('/chat/users');
export const chatWsUrl = () => {
  const token = localStorage.getItem('nslive_token') || '';
  const wsBase = BASE.replace(/^http/, 'ws');
  return `${wsBase}/ws/chat?token=${encodeURIComponent(token)}`;
};
